'use client';

import { useMutation, useQuery } from '@tanstack/react-query';

import { IMasterclass } from '@/entities/masterclass/types';
import { queryClient } from '@/shared/api/query-client';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { sendYMEvent, YM_TARGETS_ENUM } from '@/shared/const/ym';
import { useSession } from '@/shared/hooks/use-session';
import { useApi } from '@/shared/hooks/use-api';

import {
  addToLocalStorageFavorites,
  clearLocalStorageFavorites,
  getLocalStorageFavorites,
  getLocalStorageFavoritesCount,
  removeFromLocalStorageFavorites,
} from './utils';

export const useGetFavoritesQuery = () => {
  const { token } = useSession();
  const api = useApi();

  return useQuery({
    queryKey: [QUERY_KEYS.favorites],
    queryFn: async () => {
      return await api.get(`/favorites`);
    },
    select: (data) => {
      return data.data.items as IMasterclass[];
    },
    enabled: !!token,
  });
};

export const useAddMasterclassToFavoritesMutation = () => {
  const api = useApi();

  return useMutation({
    mutationFn: async (masterclassId: number) => {
      return await api.post(`/favorites/add/${masterclassId}`, {});
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.ADD_TO_FAVORITES);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favorites] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclass] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favoritesCount] });
    },
  });
};

export const useAddManyMasterclassesToFavoritesMutation = () => {
  const api = useApi();

  return useMutation({
    mutationFn: async (masterclassIds: number[]) => {
      return await api.post(`/favorites/add-many`, { data: masterclassIds });
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.ADD_TO_FAVORITES);
      clearLocalStorageFavorites();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favorites] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclass] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favoritesCount] });
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useRemoveMasterclassFromFavoritesMutation = () => {
  const api = useApi();

  return useMutation({
    mutationFn: async (masterclassId: number) => {
      return await api.delete(`/favorites/remove/${masterclassId}`);
    },
    onSuccess: () => {
      sendYMEvent(YM_TARGETS_ENUM.DELETE_FROM_FAVORITES);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclasses] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.masterclass] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favorites] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.favoritesCount] });
    },
  });
};

export const useGetFavoritesCountQuery = () => {
  const { token } = useSession();
  const api = useApi();

  return useQuery({
    queryKey: [QUERY_KEYS.favoritesCount],
    queryFn: async () => {
      return await api.get(`/favorites/count`);
    },
    select: (data) => data.data || 'пока пусто',
    enabled: !!token,
  });
};

export const useAddMasterclassesToLocalStorageFavoritesMutation = () => {
  return useMutation({
    mutationFn: async (masterclassId: number) => {
      addToLocalStorageFavorites(masterclassId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageFavorites],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageFavoritesCount],
      });
    },
  });
};

export const useRemoveMasterclassesFromLocalStorageFavoritesMutation = () => {
  return useMutation({
    mutationFn: async (masterclassId: number) => {
      removeFromLocalStorageFavorites(masterclassId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageFavorites],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.localStorageFavoritesCount],
      });
    },
  });
};

export const useGetLocalStorageFavoritesCountQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.localStorageFavoritesCount],
    queryFn: () => {
      return getLocalStorageFavoritesCount();
    },
  });
};

export const useGetLocalStorageFavoritesQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.localStorageFavorites],
    queryFn: async () => {
      return getLocalStorageFavorites();
    },
  });
};
